import { Check, Close } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import {
  GridColDef,
  GridRowSelectionModel,
  GridToolbar,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid"
import { DataGridPro } from "@mui/x-data-grid-pro"
import { deDE } from "@mui/x-data-grid/locales"
import { StyledButton } from "components/Button/StyledButton"
import { TagCloud, TagWithFrequency } from "components/TagFilters/TagCloud"
import { DeviceEutGroup, ExamCalc, Norm } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { DEFAULT_TAG_FILTER, NormTagFilterState } from "store/store"
import { isEutGroup } from "store/typeGuards"
import { v4 } from "uuid"

type AddNormsDialogProps = {
  open: boolean
  // eslint-disable-next-line no-unused-vars
  setOpen: (open: boolean) => void
  onSave: (
    // eslint-disable-next-line no-unused-vars
    arg: {
      norm: Norm
      examinations: {
        examCalc: ExamCalc
        operatingModes: string[]
        examinationId: string
        salesQuote: string
      }[]
    }[]
  ) => void
  device: DeviceEutGroup
}

const getTagIdentifier = (tag?: { id?: string | null; name: string }) => {
  if (!tag) return ""
  return tag.id && tag.id !== "" ? tag.id : tag.name.toLowerCase()
}

const matchesSearch = (examCalc: ExamCalc, searchTerm: string) => {
  if (!searchTerm) return true
  const needle = searchTerm.toLowerCase()
  if (examCalc.name.toLowerCase().includes(needle)) {
    return true
  }
  return (examCalc.tags || []).some((tag) => tag.name.toLowerCase().includes(needle))
}

const filterExamCalcsByFilter = (calculations: ExamCalc[], filter: NormTagFilterState) => {
  const activeTags = filter.selectedTagIds
  return calculations.filter((calc) => {
    if (!matchesSearch(calc, filter.searchTerm)) {
      return false
    }
    if (!activeTags.length) {
      return true
    }
    const calcTagIds = new Set((calc.tags || []).map((tag) => getTagIdentifier(tag)))
    return activeTags.every((tagId) => calcTagIds.has(tagId))
  })
}

const computeTagFrequencies = (calculations: ExamCalc[]): TagWithFrequency[] => {
  const map = new Map<string, TagWithFrequency>()
  calculations.forEach((calc) => {
    ;(calc.tags || []).forEach((tag) => {
      const id = getTagIdentifier(tag)
      if (!id) return
      const existing = map.get(id)
      if (existing) {
        existing.count += 1
      } else {
        map.set(id, {
          id,
          name: tag.name,
          count: 1,
        })
      }
    })
  })
  return Array.from(map.values())
}

const ensureActiveTagsVisible = (
  frequencies: TagWithFrequency[],
  calculations: ExamCalc[],
  filter: NormTagFilterState
) => {
  const map = new Map(frequencies.map((freq) => [freq.id, { ...freq }]))
  filter.selectedTagIds.forEach((tagId) => {
    if (!map.has(tagId)) {
      const matchingTag = calculations
        .flatMap((calc) => calc.tags || [])
        .find((tag) => getTagIdentifier(tag) === tagId)
      map.set(tagId, {
        id: tagId,
        name: matchingTag?.name ?? tagId,
        count: 0,
      })
    }
  })
  return Array.from(map.values())
}

const computeDisabledTagIds = (calculations: ExamCalc[], filter: NormTagFilterState) => {
  const disabled: string[] = []
  const baseTags = computeTagFrequencies(calculations)
  baseTags.forEach((tag) => {
    if (filter.selectedTagIds.includes(tag.id)) return
    const nextFilter: NormTagFilterState = {
      ...filter,
      selectedTagIds: [...filter.selectedTagIds, tag.id],
    }
    const result = filterExamCalcsByFilter(calculations, nextFilter)
    if (result.length === 0) {
      disabled.push(tag.id)
    }
  })
  return disabled
}

const SEARCH_DEBOUNCE_MS = 350

type NormDetailPanelProps = {
  norm: Norm
  detailFilterKey: string
  filter: NormTagFilterState
  setNormTagFilter: (
    payload:
      | { scope: "admin"; value: Partial<NormTagFilterState> }
      | { scope: "project"; projectId: string; value: Partial<NormTagFilterState> }
  ) => void
  resetNormTagFilter: (
    payload: { scope: "admin" } | { scope: "project"; projectId: string }
  ) => void
  selectedSelection: GridRowSelectionModel
  onSelectionChange: (selection: GridRowSelectionModel) => void
}

const NormDetailPanel = ({
  norm,
  detailFilterKey,
  filter,
  setNormTagFilter,
  resetNormTagFilter,
  selectedSelection,
  onSelectionChange,
}: NormDetailPanelProps) => {
  const [searchDraft, setSearchDraft] = useState(filter.searchTerm)

  useEffect(() => {
    setSearchDraft(filter.searchTerm)
  }, [filter.searchTerm, detailFilterKey])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setNormTagFilter({
        scope: "project",
        projectId: detailFilterKey,
        value: { searchTerm: searchDraft },
      })
    }, SEARCH_DEBOUNCE_MS)

    return () => window.clearTimeout(timer)
  }, [searchDraft, detailFilterKey, setNormTagFilter])

  const calculations = norm.examinationCalculations || []
  const filteredCalcs = filterExamCalcsByFilter(calculations, filter)
  const pinnedCalcs = calculations.filter(
    (calc) =>
      selectedSelection.includes(calc.id) && !filteredCalcs.some((item) => item.id === calc.id)
  )
  const rowsToRender = [...filteredCalcs, ...pinnedCalcs]

  const allFrequencies = computeTagFrequencies(calculations)
  const relevantFrequencies = computeTagFrequencies(filteredCalcs)
  const tagsForCloud = filter.showOnlyRelevant
    ? ensureActiveTagsVisible(relevantFrequencies, calculations, filter)
    : ensureActiveTagsVisible(allFrequencies, calculations, filter)
  const disabledTagIds = filter.showOnlyRelevant ? [] : computeDisabledTagIds(calculations, filter)

  const handleToggleTag = useCallback(
    (tagId: string) => {
      const isActive = filter.selectedTagIds.includes(tagId)
      const nextIds = isActive
        ? filter.selectedTagIds.filter((id) => id !== tagId)
        : [...filter.selectedTagIds, tagId]
      setNormTagFilter({
        scope: "project",
        projectId: detailFilterKey,
        value: { selectedTagIds: nextIds },
      })
    },
    [filter.selectedTagIds, detailFilterKey, setNormTagFilter]
  )

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchDraft(event.target.value)
  }

  const handleToggleRelevant = (event: ChangeEvent<HTMLInputElement>) => {
    setNormTagFilter({
      scope: "project",
      projectId: detailFilterKey,
      value: { showOnlyRelevant: event.target.checked },
    })
  }

  const handleClearFilters = () => {
    resetNormTagFilter({ scope: "project", projectId: detailFilterKey })
    setSearchDraft(DEFAULT_TAG_FILTER.searchTerm)
  }

  const isResetDisabled =
    searchDraft === DEFAULT_TAG_FILTER.searchTerm &&
    filter.selectedTagIds.length === 0 &&
    filter.showOnlyRelevant

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "name", headerName: "Bezeichnung", flex: 3 },
      {
        field: "tags",
        headerName: "Tags",
        flex: 2,
        renderCell: (params) => {
          const tags = params.row.tags || []
          if (!tags.length) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )
          }

          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
              {tags.map((tag: { id: string; name: string }) => {
                const tagId = getTagIdentifier(tag)
                const isActive = filter.selectedTagIds.includes(tagId)
                const isDisabled = !isActive && disabledTagIds.includes(tagId)

                return (
                  <Chip
                    key={`${params.row.id}-${tagId}`}
                    label={tag.name}
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation()
                      if (!isDisabled) {
                        handleToggleTag(tagId)
                      }
                    }}
                    disabled={isDisabled}
                    sx={{
                      borderRadius: 9999,
                      px: 1.5,
                      py: 0.5,
                      fontWeight: isActive ? 600 : 400,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      bgcolor: isActive ? "success.main" : "background.paper",
                      color: isActive ? "success.contrastText" : "text.primary",
                      border: isActive ? "none" : "1px solid",
                      borderColor: isActive ? "transparent" : "divider",
                      transition: "transform 0.12s ease, background-color 0.12s ease",
                      transform: isActive ? "scale(1.05)" : "none",
                      "&:hover": !isDisabled
                        ? isActive
                          ? {
                              bgcolor: "error.main",
                              color: "error.contrastText",
                            }
                          : {
                              bgcolor: "primary.light",
                              color: "primary.contrastText",
                            }
                        : {},
                      "& .MuiChip-label": {
                        px: 0.5,
                      },
                    }}
                  />
                )
              })}
            </Box>
          )
        },
      },
      {
        field: "isAccredited",
        headerName: "Akkreditiert",
        flex: 1,
        renderCell: (params) =>
          params.row.isAccredited ? <Check color="success" /> : <Close color="error" />,
      },
    ],
    [disabledTagIds, filter.selectedTagIds, handleToggleTag]
  )

  return (
    <Box sx={{ ml: 5, p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <TextField
          size="medium"
          label="Suche"
          value={searchDraft}
          onChange={handleSearchChange}
          sx={{ flex: 1 }}
        />
        <FormControlLabel
          control={<Switch checked={filter.showOnlyRelevant} onChange={handleToggleRelevant} />}
          label="Nur relevante Tags"
        />
        <Button onClick={handleClearFilters} disabled={isResetDisabled} variant="outlined">
          Zurücksetzen
        </Button>
      </Stack>

      <TagCloud
        tags={tagsForCloud}
        activeTagIds={filter.selectedTagIds}
        onToggle={handleToggleTag}
        disabledTagIds={disabledTagIds}
      />

      <DataGridPro
        rows={rowsToRender}
        columns={columns}
        getRowId={(examRow) => examRow.id}
        checkboxSelection
        rowSelectionModel={selectedSelection}
        onRowSelectionModelChange={onSelectionChange}
        getRowHeight={() => "auto"}
        keepNonExistentRowsSelected
        disableRowSelectionOnClick
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
      />
    </Box>
  )
}

export const AddNormsToProjectDialog = (props: AddNormsDialogProps) => {
  const { open, setOpen, onSave, device } = props
  const { norms, normFilters } = useStoreState((state) => ({
    norms: state.norms,
    normFilters: state.normFilters,
  }))

  const { fetchNorms, setNormTagFilter, resetNormTagFilter } = useStoreActions((actions) => ({
    fetchNorms: actions.fetchNorms,
    setNormTagFilter: actions.setNormTagFilter,
    resetNormTagFilter: actions.resetNormTagFilter,
  }))

  const filterKey = device?.id || "global"

  useEffect(() => {
    const loadNorms = async () => {
      try {
        await fetchNorms({ fresh: false })
      } catch (err) {
        console.error("Error loading norms:", err)
      }
    }
    loadNorms()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [selectedExaminationsMap, setSelectedExaminationsMap] = useState<
    Record<string, GridRowSelectionModel>
  >({})

  const [validationError, setValidationError] = useState<string>("")

  useEffect(() => {
    if (device && device.examinations && device.examinations.length > 0 && norms.length > 0) {
      const newSelectedExaminationsMapUpdate: Record<string, GridRowSelectionModel> = {}
      let newSelectedNormRowIdsUpdate: GridRowSelectionModel = []

      device.examinations.forEach((deviceExam) => {
        if (deviceExam.norm && deviceExam.examinationConfig) {
          const targetExamCalc = deviceExam.norm.examinationCalculations.find(
            (ec) => ec.name === deviceExam.name
          )

          if (targetExamCalc) {
            // Initialize examination selection for this norm
            if (!newSelectedExaminationsMapUpdate[deviceExam.norm.id]) {
              newSelectedExaminationsMapUpdate[deviceExam.norm.id] = []
            }

            const currentNormExams = newSelectedExaminationsMapUpdate[deviceExam.norm.id]
            if (!currentNormExams.includes(targetExamCalc.id)) {
              newSelectedExaminationsMapUpdate[deviceExam.norm.id] = [
                ...currentNormExams,
                targetExamCalc.id,
              ]
            }

            // Add norm to selected norms if not already present
            if (!newSelectedNormRowIdsUpdate.includes(deviceExam.norm.id)) {
              newSelectedNormRowIdsUpdate = [...newSelectedNormRowIdsUpdate, deviceExam.norm.id]
            }
          }
        }
      })

      setSelectedExaminationsMap(newSelectedExaminationsMapUpdate)
    } else if (open && (!device.examinations || device.examinations.length === 0)) {
      setSelectedExaminationsMap({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.examinations, norms, open]) // Added 'open' to dependencies

  // Reset validation error when dialog opens
  useEffect(() => {
    if (open) {
      setValidationError("")
    }
  }, [open])

  const validateSelection = (): boolean => {
    // Check if examinations are selected
    let hasExaminations = false
    for (const normId in selectedExaminationsMap) {
      const selectedExamIds = selectedExaminationsMap[normId]
      if (selectedExamIds && selectedExamIds.length > 0) {
        hasExaminations = true
        break
      }
    }

    if (!hasExaminations) {
      setValidationError("Bitte wählen Sie mindestens ein Prüfverfahren aus.")
      return false
    }

    setValidationError("")
    return true
  }

  const handleSave = () => {
    if (!validateSelection()) {
      return
    }

    const resultOutput: {
      norm: Norm
      examinations: {
        examCalc: ExamCalc
        operatingModes: string[]
        examinationId: string
        salesQuote: string
      }[]
    }[] = []

    // Extract operating modes from device if available
    const deviceOperatingModes =
      device && isEutGroup(device)
        ? device.deviceDataGroup
            .flatMap((group) => group.deviceData)
            .filter((data) => data.isOperatingMode)
            .flatMap((data) => data.data || [])
            .filter((d) => d != null)
            .map((d) => d.value)
            .filter((value) => value != null && value !== "")
        : []

    for (const normId in selectedExaminationsMap) {
      const selectedExamIds = selectedExaminationsMap[normId]
      if (selectedExamIds && selectedExamIds.length > 0) {
        const norm = norms.find((n) => n.id === normId)
        if (norm) {
          const selectedExamsForNorm = norm.examinationCalculations
            .filter((ec) => selectedExamIds.includes(ec.id))
            .map((examCalc) => {
              // For new examinations, add operating modes from device
              return {
                examCalc: examCalc,
                operatingModes: deviceOperatingModes,
                examinationId: v4(),
                salesQuote: "",
              }
            })

          if (selectedExamsForNorm.length > 0) {
            resultOutput.push({
              norm: norm,
              examinations: selectedExamsForNorm,
            })
          }
        }
      }
    }
    onSave(resultOutput)

    setOpen(false)
    // setSelectedNormRowIds([])
    setSelectedExaminationsMap({})
  }

  const columnsNorms = [
    { field: "name", headerName: "Bezeichnung" },
    { field: "branch", headerName: "Branche" },
    { field: "label", headerName: "Titel" },
    { field: "date", headerName: "Jahr" },
    { field: "revision", headerName: "Revision" },
    {
      field: "isOutdated",
      headerName: "Veraltet",

      renderCell: (params: any) => {
        return params.row.isOutdated ? <Check color="success" /> : <Close color="error" />
      },
    },
    {
      field: "isHarmonized",
      headerName: "Harmonisiert",

      renderCell: (params: any) => {
        return params.row.isHarmonized ? <Check color="success" /> : <Close color="error" />
      },
    },
  ]

  const getDetailPanelContent = useCallback(
    ({ row }: { row: Norm }) => {
      const normId = row.id
      const detailFilterKey = `${filterKey}:${normId}`
      const currentFilter = normFilters.project[detailFilterKey] || { ...DEFAULT_TAG_FILTER }
      const currentSelection = selectedExaminationsMap[normId] || []

      return (
        <NormDetailPanel
          norm={row}
          detailFilterKey={detailFilterKey}
          filter={currentFilter}
          setNormTagFilter={setNormTagFilter}
          resetNormTagFilter={resetNormTagFilter}
          selectedSelection={currentSelection}
          onSelectionChange={(newSelection) => {
            setSelectedExaminationsMap((prev) => ({
              ...prev,
              [normId]: newSelection,
            }))
            setValidationError("")
          }}
        />
      )
    },
    [selectedExaminationsMap, filterKey, setNormTagFilter, resetNormTagFilter, normFilters.project]
  )

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle>Prüfverfahren hinzufügen</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Wählen Sie die Prüfverfahren aus, die Sie hinzufügen möchten.
        </DialogContentText>

        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        <DataGridPro
          slots={{
            toolbar: () => (
              <GridToolbarContainer>
                <GridToolbar />
                <GridToolbarQuickFilter />
              </GridToolbarContainer>
            ),
          }}
          rows={norms.filter((n) => !n.isDeleted)}
          columns={columnsNorms}
          getRowId={(row) => row.id}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={() => "auto"}
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          hideFooterPagination={false}
          autosizeOptions={{
            includeOutliers: true,
            includeHeaders: true,
          }}
          autosizeOnMount
          disableAutosize={false}
        />
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={() => setOpen(false)} variant="contained" />
        <StyledButton buttonType="save" onClick={handleSave} variant="contained" />
      </DialogActions>
    </Dialog>
  )
}
